// angular imports
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';

// 3rd party imports
import { Subscription } from 'rxjs';

// devextreme imports
import { DxFileUploaderComponent, DxFormComponent } from 'devextreme-angular';

// cc-common imports
import { Utils, IconConstants } from 'cc-common';

// component imports
import { MultiColumnSelectorComponent } from '../../../management-common/component/common/multi-column-selector/multi-column-selector.component';

// service imports
import { CommunityService } from '../../../_services/data-services/community.service';
import { ContextManagerService } from '../../../_services/data-services/context-manager.service';
import { FileReaderService } from '../../../_services/shared-services/file-reader.service';
import { HelperService } from '../../../_services/shared-services/helper.service';
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { WorkflowPaymentRequestService } from '../../_services/workflow-payment-request.service';

// model imports
import { WFParametersDto } from '../../../shared-workflow-module/_models/wf-parameter-dto';
import { WFESpecialHandlingResultDto } from '../../../shared-workflow-module/_models/wfe-special-handling-result-dto';
import { CommunitySelectorResultDto } from '../../../_models/community-selector-result-dto';
import { SimplePaymentRequestEditorResultDto } from '../../_models/simple-payment-request-editor-result-dto';
import { SimplePaymentRequestRawDto } from '../../_models/simple-payment-request-raw-dto';
import { AttachmentParameterDto } from '../../../_models/attachment-parameter-dto';

@Component({
    selector: 'app-workflow-payment-request-simple',
    templateUrl: './workflow-payment-request-simple.component.html',
    styleUrls: ['./workflow-payment-request-simple.component.scss']
})
export class WorkflowPaymentRequestSimpleComponent implements OnInit, OnDestroy {
    @ViewChild('workflowSubmitInvoiceEditor', { static: false }) workflowSubmitInvoiceEditor: DxFormComponent;
    @ViewChild('communitySelector', { static: false }) communitySelector: MultiColumnSelectorComponent;
    @ViewChild('fileUploader', { static: false }) fileUploader: DxFileUploaderComponent;

    @Input() selectedCommunityId: number;

    @Output() onInvoiceSelection = new EventEmitter<any>();
    @Output() onInvoiceOptionChange = new EventEmitter<string>();
    @Output() onCommunitySelectionChange = new EventEmitter<CommunitySelectorResultDto>();
    @Output() onAdvanceModeSelectionChange = new EventEmitter<boolean>();

    paymentEditor: SimplePaymentRequestRawDto;

    communityList: CommunitySelectorResultDto[] = [];
    communityColumns: any[];
    communityGridBoxValue: number[];
    communityId: number;
    isCommunityContext = false;

    specialHandlingSource: WFESpecialHandlingResultDto[];
    isNotesRequired = false;

    uploadedFile: File[] = [];
    invoiceOptions: any[] = [];
    selectedInvoiceOption: any;

    contextManagerSubscription: Subscription;

    iconTrash = IconConstants.DELETE;

    loadingVisible = false;

    constructor(
        private readonly workflowPaymentRequestService: WorkflowPaymentRequestService,
        private readonly errorService: ServerErrorService,
        private readonly communityService: CommunityService,
        private readonly fileReader: FileReaderService,
        private readonly contextManagerService: ContextManagerService,
        private readonly helperService: HelperService
    ) {
        this.communityColumns = this.communityService.getCommunitySelectorColumns();
    }

    ngOnInit() {
        this.getInvoiceOptions();
        this.getSelectedContext();
        this.contextManagerSubscription = this.contextManagerService.getContext().subscribe(() => {
            this.getSelectedContext();
        });
    }

    ngOnDestroy() {
        this.contextManagerSubscription.unsubscribe();
    }

    onSubmitFormContentReady(): void {
        if (Utils.notNullAndDefined(this.paymentEditor.communityId)) {
            this.communityGridBoxValue = [this.paymentEditor.communityId];
        }
    }

    onCommunitySelection(selectedObject: any): void {
        if (Utils.notNullAndDefined(selectedObject) && Utils.notNullOrEmpty(selectedObject.selectedRow)) {
            const selectedCommunity = selectedObject.selectedRow[0];
            this.communityId = selectedCommunity.communityId;
            this.paymentEditor.communityId = selectedCommunity.communityId;
            this.onCommunitySelectionChange.emit(selectedCommunity);
            this.getSpecialHandling();
        } else {
            this.onCommunitySelectionChange.emit(null);
            this.communityId = null;
            this.paymentEditor.communityId = null;
        }
    }

    onSpecialHandlingValueChange(): void {
        if (Utils.notNullOrEmpty(this.specialHandlingSource)) {
            const selectedSpecialHandling = this.specialHandlingSource.find((d) =>
                Utils.equals(d.specialHandlingId, this.paymentEditor.specialHandlingId)
            );
            if (Utils.notNullAndDefined(selectedSpecialHandling)) {
                if (selectedSpecialHandling.displayText.includes('Special Instructions')) {
                    this.isNotesRequired = true;
                } else {
                    this.isNotesRequired = false;
                }
            }
        }
    }

    onFileUpload(): void {
        if (Utils.notNullOrEmpty(this.uploadedFile)) {
            this.fileReader.readFile(this.uploadedFile[0]).subscribe((result: AttachmentParameterDto) => {
                this.paymentEditor.fileBase64String = result.attachmentBase64;
                this.paymentEditor.fileName = result.fileName;

                const blob: Blob = new Blob([this.uploadedFile[0]]);
                const fileURL = this.helperService.getFileUrlFromBlob(blob);
                this.onInvoiceSelection.emit(fileURL);
            });
        }
    }

    onInvoiceOptionChanged(): void {
        this.onInvoiceOptionChange.emit(this.selectedInvoiceOption);
    }

    onBrowseClick(): void {
        const uploader: any = this.fileUploader.instance;
        uploader._isCustomClickEvent = true;
        uploader._$fileInput[0].click();
    }

    onDeleteFile(): void {
        this.uploadedFile = [];
        this.paymentEditor.fileBase64String = null;
        this.paymentEditor.fileName = null;
        this.onInvoiceSelection.emit(null);
    }

    private getCommunities(): void {
        this.communityService.getCommunities().subscribe(
            (result: CommunitySelectorResultDto[]) => {
                this.communityList = result;
                if (Utils.notNullAndDefined(this.communityId)) {
                    const selectedCommunity = this.communityList.find((d) =>
                        Utils.equals(d.communityId, this.communityId)
                    );
                    if (Utils.notNullAndDefined(selectedCommunity)) {
                        this.onCommunitySelectionChange.emit(selectedCommunity);
                    }
                }
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private getSimpleEditor(): void {
        this.loadingVisible = true;
        this.workflowPaymentRequestService.getSimpleEditor().subscribe(
            (result: SimplePaymentRequestEditorResultDto) => {
                this.paymentEditor = result.paymentRequest;

                if (Utils.notNullAndDefined(this.communityId)) {
                    this.communityGridBoxValue = [this.communityId];
                    this.paymentEditor.communityId = this.communityId;
                    this.getSpecialHandling();
                }

                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private getSpecialHandling(clearCache = false): void {
        const baseParams: WFParametersDto = new WFParametersDto();
        baseParams.communityId = this.communityId;
        baseParams.clearCache = clearCache;

        this.workflowPaymentRequestService.getSpecialHandlings(baseParams).subscribe(
            (result: WFESpecialHandlingResultDto[]) => {
                this.specialHandlingSource = result;
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private getSelectedContext(): void {
        const selectedContext = this.contextManagerService.getSelectedContext();

        if (Utils.notNullAndDefined(selectedContext) && Utils.notNullAndDefined(selectedContext.communityId)) {
            this.communityId = selectedContext.communityId;
            this.isCommunityContext = true;
        }

        if (Utils.notNullAndDefined(this.selectedCommunityId)) {
            this.communityId = this.selectedCommunityId;
        }
        this.getCommunities();
        this.getSimpleEditor();
    }

    private getInvoiceOptions(): void {
        this.invoiceOptions = [
            { key: 'upload_vendor_invoice', text: 'Upload Vendor Invoice' },
            {
                key: 'generate_payment_request',
                text: 'Generate a Payment Request (Upload supporting document where applicable)'
            }
        ];
        this.selectedInvoiceOption = 'upload_vendor_invoice';
        this.onInvoiceOptionChanged();
    }

    public getPaymentEditor(): SimplePaymentRequestRawDto {
        return Utils.notNullAndDefined(this.paymentEditor) ? this.paymentEditor : null;
    }

    public isNotesMandatory(): boolean {
        return this.isNotesRequired;
    }

    public resetEditor(): void {
        this.paymentEditor = new SimplePaymentRequestRawDto();
        this.communityId = null;
        this.specialHandlingSource = [];
        this.isNotesRequired = false;
        this.communityList = [];
        this.uploadedFile = [];
        this.communityGridBoxValue = [];
        this.onInvoiceSelection.emit(null);
        if (Utils.notNullAndDefined(this.communitySelector)) {
            this.communitySelector.resetSelector();
        }
        this.workflowSubmitInvoiceEditor.instance.repaint();
        this.getSelectedContext();
    }
}
